<script setup lang="ts">
import BackComponent from '@/components/BackComponent.vue';
import BottomBarComponent from '@/components/BottomBarComponent.vue';
import InputCardComponent from '@/components/InputCardComponent.vue';
import NextComponent from '@/components/NextComponent.vue';
import { useAgreementStore } from '@/stores/agreement';
import { useConfigStore } from '@/stores/config';
import local from '@/stores/local';
import { useMessageStore } from '@/stores/messageStore';
import { useVehicleStore } from '@/stores/vehicle';
import { resetEverything } from '@/utils/reset';
import { estimateOldMileage, getMonthName } from '@/helpers';
import type { Model, Brand, Engine, ManualVehicle } from '@/types';
import axios from 'axios';
import { storeToRefs } from 'pinia';
import { computed, onMounted, ref, watch } from 'vue';
import { onBeforeRouteLeave } from 'vue-router';

const vehicleStore = useVehicleStore()
const agreementStore = useAgreementStore()
const message = useMessageStore()
const { agreement, calculation } = storeToRefs(useAgreementStore())
const { config } = storeToRefs(useConfigStore())
const currentYear = new Date().getFullYear()

const loadingBrands = ref(false)
const loadingModels = ref(false)
const loadingEngines = ref(false)
const disabled = ref()
const brands = ref<Brand[]>([])
const models = ref<Model[]>([])
const engines = ref<Engine[]>([])

const monthOptions = computed(() => Array.from({ length: 12 }, (_, i) => ({ value: i, title: getMonthName(i) })))

async function onReset() {
  await resetEverything()
  disabled.value = false
}

async function getBrands() {
  loadingBrands.value = true

  try {
    const { data } = await axios.get('proxy/brands')

    brands.value = data
  } catch (error) {
    console.error(error)
    message.addError('Der skete en fejl ved at hente mærker')
  } finally {
    loadingBrands.value = false
  }
}

async function getModels(brandId: number) {
  loadingModels.value = true

  try {
    const { data } = await axios.get(`proxy/brands/${brandId}/models`)

    models.value = data
  } catch (error) {
    console.error(error)
    message.addError('Der skete en fejl ved at hente modeller')
  } finally {
    loadingModels.value = false
  }
}

async function getEngines(brandId: number) {
  loadingEngines.value = true

  try {
    const { data } = await axios.get(`proxy/brands/${brandId}/engines`)

    engines.value = data
  } catch (error) {
    console.error(error)
    message.addError('Der skete en fejl ved at hente motorer')
  } finally {
    loadingEngines.value = false
  }
}

function onSelectBrand(brand: Brand | undefined) {
  vehicleStore.manualVehicle.brand = brand
  vehicleStore.manualVehicle.model = undefined
  vehicleStore.manualVehicle.engine = undefined

  agreement.value.brandId = brand?.id

  calculation.value = null
  local.removeItem('calculation')

  if (brand) {
    getModels(brand.id)
    getEngines(brand.id)
  }
}

function onSelectModel(model: Model | undefined) {
  vehicleStore.manualVehicle.model = model
  agreement.value.modelId = model?.id

  //agreementStore.calculate()
}

function onSelectEngine(engine: Engine | undefined) {
  vehicleStore.manualVehicle.engine = engine
  agreement.value.engineId = engine?.id

  //agreementStore.calculate()
}

watch(() => agreement.value.registrationYear, (value) => {
  if (value?.toString().length === 4) {
    agreementStore.calculate()

    if (value?.toString().length === 4 && !agreement.value.oldMileage) {
      agreement.value.oldMileage = estimateOldMileage(value, config.value?.maxStartMileage as number)
    }
  }
})

onMounted(() => {
  getBrands()

  if (vehicleStore.manualVehicle.brand?.id) {
    getModels(vehicleStore.manualVehicle.brand.id)
    getEngines(vehicleStore.manualVehicle.brand.id)
  }
})
</script>

<template>
  <v-container>
    <v-row>
      <v-col>
        <InputCardComponent label="Vælg mærke, model og motor">
          <div class="d-flex flex-column" style="gap: 1rem">
            <v-autocomplete
              :model-value="vehicleStore.manualVehicle.brand"
              :items="brands"
              :loading="loadingBrands"
              label="Vælg mærke"
              item-value="id"
              item-title="name"
              :autofocus="!vehicleStore.manualVehicle.brand"
              auto-select-first
              return-object
              hide-details
              @update:model-value="onSelectBrand"
            />
            <v-autocomplete
              v-show="vehicleStore.manualVehicle.brand"
              :model-value="vehicleStore.manualVehicle.model"
              :items="models"
              label="Vælg model"
              item-value="id"
              item-title="name"
              :autofocus="!vehicleStore.manualVehicle.model"
              auto-select-first
              return-object
              hide-details
              @update:model-value="onSelectModel"
            />
            <v-autocomplete
              v-show="vehicleStore.manualVehicle.model"
              :model-value="vehicleStore.manualVehicle.engine"
              :items="engines"
              label="Vælg motor"
              item-value="id"
              item-title="name"
              :autofocus="!vehicleStore.manualVehicle.engine"
              auto-select-first
              return-object
              hide-details
              @update:model-value="onSelectEngine"
            />
          </div>
        </InputCardComponent>
      </v-col>
    </v-row>
    <v-slide-x-transition>
      <v-row v-if="vehicleStore.manualVehicle.brand && vehicleStore.manualVehicle.model && vehicleStore.manualVehicle.engine">
        <v-col>
          <InputCardComponent label="Hvornår er bilen fra?">
            <v-otp-input
              v-model.number="agreementStore.agreement.registrationYear"
              length="4"
              autofocus
            />
          </InputCardComponent>
        </v-col>
      </v-row>
    </v-slide-x-transition>
    <v-slide-x-transition>
      <v-row v-if="agreementStore.agreement.registrationYear?.toString().length === 4">
        <v-col>
          <InputCardComponent label="Hvilken måned er bilen registreret?">
            <v-select
              v-model="agreementStore.agreement.registrationMonth"
              :items="monthOptions"
              item-value="value"
              item-title="title"
              label="Vælg måned"
              hide-details
            />
          </InputCardComponent>
        </v-col>
      </v-row>
    </v-slide-x-transition>
  </v-container>

  <BottomBarComponent>
    <BackComponent to="/agreement/vehicle" />
    <NextComponent
      to="/agreement/type"
      :disabled="!vehicleStore.manualVehicle.brand ||
      !vehicleStore.manualVehicle.model ||
      !vehicleStore.manualVehicle.engine ||
        agreementStore.agreement.registrationYear !== undefined &&
        agreementStore.agreement.registrationYear > (currentYear + 1)
      ||
        !agreementStore.isOkay
      ||
      agreementStore.agreement.registrationYear?.toString().length !== 4
      ||
      typeof agreementStore.agreement.registrationMonth !== 'number'"
      @click="vehicleStore.isManualInput = true"
    />
  </BottomBarComponent>
</template>

<style>
#registration-input {
  text-transform: uppercase;
  font-size: 2rem;
  text-align: center;
}
</style>
